import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../clients/supabaseClient';
import StatusBadge from '../components/StatusBadge';
import type { CustomerOrder } from '../services/customerService';
import { fetchOrdersForCustomerFromDb } from '../services/customerService';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

const fmtDateTime = (d?: string | null) => d ? new Date(d).toLocaleString() : '—';

type CustomerMeta = { first_name: string | null; last_name: string | null };

export default function CustomerOrders() {
  const { id } = useParams<{ id: string }>();

  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState<CustomerOrder[]>([]);
  const [customer, setCustomer] = useState<CustomerMeta | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 25;

  const totalItems = orders.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedOrders = orders.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      setLoading(true);
      const [orderRows, customerRow] = await Promise.all([
        fetchOrdersForCustomerFromDb(id),
        supabase
            .from('customers')
            .select('first_name, last_name')
            .eq('id', id)
            .single()
            .then(({ data }) => data as CustomerMeta | null),
      ]);
      setOrders(orderRows);
      setCustomer(customerRow);
      setLoading(false);
    };
    void load();
  }, [id]);

  const customerName = customer
      ? `${customer.first_name ?? ''} ${customer.last_name ?? ''}`.trim() || 'Customer'
      : 'Customer';

  return (
      <div className="space-y-6">
        {/* Breadcrumb */}
        <div className="flex items-center justify-between">
          <nav className="text-sm text-stone-500" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2">
              <li><Link to="/customers" className="hover:text-stone-900 transition">Customers</Link></li>
              <li className="text-stone-300">/</li>
              <li className="text-stone-900 font-medium">{customerName}</li>
              <li className="text-stone-300">/</li>
              <li className="text-stone-900">Orders</li>
            </ol>
          </nav>
          <Link to="/customers"
                className="inline-flex items-center gap-1.5 rounded-lg border border-stone-300 px-3 py-2 text-sm text-stone-600 hover:bg-stone-50 transition">
            <ChevronLeftIcon className="h-3.5 w-3.5" />
            Back to Customers
          </Link>
        </div>

        {/* Table */}
        <section className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
          <div className="px-5 py-3 border-b border-stone-200 flex items-center justify-between">
            <div>
              <h2 className="font-semibold text-stone-900">Orders</h2>
              {customer && (
                  <p className="text-xs text-stone-500 mt-0.5">{customerName}</p>
              )}
            </div>
            <span className="text-sm text-stone-500">{orders.length} total</span>
          </div>

          {loading ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    {['Order Ref', 'Date Ordered', 'Status', 'Delivered At'].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">{h}</th>
                    ))}
                  </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                  {Array.from({ length: 5 }).map((_, i) => (
                      <tr key={i} className="animate-pulse">
                        <td className="px-4 py-3"><div className="h-4 w-28 bg-stone-100 rounded" /></td>
                        <td className="px-4 py-3"><div className="h-4 w-36 bg-stone-100 rounded" /></td>
                        <td className="px-4 py-3"><div className="h-5 w-28 bg-stone-100 rounded-full" /></td>
                        <td className="px-4 py-3"><div className="h-4 w-36 bg-stone-100 rounded" /></td>
                      </tr>
                  ))}
                  </tbody>
                </table>
              </div>
          ) : paginatedOrders.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-stone-500">
                No orders found for this customer.
              </div>
          ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Order Ref</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Date Ordered</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Delivered At</th>
                  </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                  {paginatedOrders.map(o => (
                      <tr key={o.id} className="hover:bg-stone-50 transition-colors">
                        <td className="px-4 py-3">
                          <Link to={`/orders/${o.id}`}
                                className="font-medium text-stone-900 hover:text-[#ee2b74] transition">
                            {o.reference}
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-stone-500 text-xs">{fmtDateTime(o.created_at)}</td>
                        <td className="px-4 py-3"><StatusBadge status={o.status} /></td>
                        <td className="px-4 py-3 text-stone-500 text-xs">{fmtDateTime(o.delivered_at)}</td>
                      </tr>
                  ))}
                  </tbody>
                </table>
                {totalPages > 1 && (
                    <div className="flex items-center justify-between border-t border-stone-200 bg-stone-50/30 px-4 py-3">
                      <p className="text-sm text-stone-600">
                        Showing <span className="font-medium">{totalItems === 0 ? 0 : startIndex + 1}</span> to{' '}
                        <span className="font-medium">{Math.min(startIndex + ITEMS_PER_PAGE, totalItems)}</span>{' '}
                        of <span className="font-medium">{totalItems}</span> orders
                      </p>
                      <nav className="flex items-center gap-1">
                        <button
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            className="rounded-lg border border-stone-300 bg-white p-2 shadow-sm transition-colors hover:bg-stone-50 disabled:opacity-40"
                        >
                          <ChevronLeftIcon className="h-4 w-4" />
                        </button>
                        <span className="px-2 text-xs font-medium text-stone-600">
                Page {currentPage} of {totalPages}
            </span>
                        <button
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                            className="rounded-lg border border-stone-300 bg-white p-2 shadow-sm transition-colors hover:bg-stone-50 disabled:opacity-40"
                        >
                          <ChevronRightIcon className="h-4 w-4" />
                        </button>
                      </nav>
                    </div>
                )}
              </div>
          )}
        </section>
      </div>
  );
}